/**
 * R3-C part 2 — text correction from CAD (the oracle's confirmed cases).
 *
 * The 2026-08-28 oracle run proved three extractor defect classes against
 * CAD ground truth, each with an exact reconstruction:
 *  - SPACE-VARIANT: an extractor label equal to exactly one CAD label under
 *    space-insensitive comparison (Evron: `2.4DCCB75` vs CAD `2.4 DCCB75`)
 *    is the SAME label — the extractor's normalizeLabel strips spaces by
 *    design. Recognized as agreement, counted, and NEVER split. (The oracle
 *    initially misread these as defects; the test suite caught it.)
 *  - TRUNCATION: an extractor label that is an unambiguous prefix/suffix of
 *    exactly one CAD label → ADOPT the CAD text and position.
 *  - CONFIRMED MERGE: an extractor label that is exactly CAD-A + CAD-B
 *    (Kfar: `TYPE_1_S06+_12DEG10.13`) → SPLIT into two entities at their
 *    CAD positions.
 *  - SWALLOWED LABEL: a CAD label with no extractor presence → ADD it, but
 *    only when it is unique in the CAD and classifies to a topology level
 *    (never modules/infrastructure — that would flood the layout with
 *    thousands of type annotations).
 *
 * Precedence matters: space-insensitive adoption BEFORE split (an Evron
 * space-loss label must not be torn in two). Everything is reversible via
 * `entity.cadFix` provenance; `undoCadFixes` restores the pre-fix layout.
 *
 * Pure computation + thin store wiring, mirroring dxfLabelSnap.
 */

import { useProjectStore } from '../stores/projectStore';
import { fetchIngestJson } from '../hooks/useDxfIngestPoller';
import {
  matchPairs, fitPairsRobust, applyDxfMatrix, normalizeLabel, correctedTextPoint,
} from './dxfRegistration';
import { classifyLabel, normalizeLabel as classifyNormalize } from '../domain/layout/classify';
import type { DxfTextEntry } from './dxfRegistration';
import type { LayoutTextEntity, SiteLayout, ClassificationPattern } from '../domain/layout/types';
import type { Overlay } from '../stores/projectStore';

export interface FixStats {
  adoptions: number;
  splits: number;
  additions: number;
  skippedAmbiguous: number;
  /** Extractor labels recognized as space-variants of a CAD label (same
   *  label, different whitespace canon) — informational, nothing changed. */
  spaceVariants: number;
}

const ADD_LEVELS_EXCLUDED = new Set(['module', 'infrastructure']);

/** Pure: apply CAD text corrections. Exported for tests. */
export function computeCadFixes(
  entities: LayoutTextEntity[],
  texts: DxfTextEntry[],
  customPatterns?: ClassificationPattern[],
): { entities: LayoutTextEntity[]; stats: FixStats } | null {
  const pairs = matchPairs(texts, entities);
  const fit = fitPairsRobust(pairs);
  if (!fit) return null;
  const m = fit.matrix;
  const scale = Math.hypot(m[0], m[3]);

  // CAD indexes. `all` keeps EVERY instance (position disambiguation by
  // proximity); `spaceless` maps despaced-key → set of CAD keys.
  const cadAll = new Map<string, DxfTextEntry[]>();
  for (const t of texts) {
    const k = normalizeLabel(t.t);
    if (!k) continue;
    const arr = cadAll.get(k);
    if (arr) arr.push(t); else cadAll.set(k, [t]);
  }
  const spaceless = new Map<string, Set<string>>();
  for (const k of cadAll.keys()) {
    const sk = k.replace(/ /g, '');
    const set = spaceless.get(sk);
    if (set) set.add(k); else spaceless.set(sk, new Set([k]));
  }
  const extKeys = new Set(entities.map((e) => normalizeLabel(e.normalizedText)).filter(Boolean));

  // Text positions must carry the fit's solved anchor bias (the matrix was
  // fitted against corrected anchors); geometry never does.
  const toPage = (t: DxfTextEntry): [number, number] => {
    const [bx, by] = correctedTextPoint(t, normalizeLabel(t.t), fit.bias);
    return applyDxfMatrix(m, bx, by);
  };
  const nearestInstance = (key: string, nearPt: [number, number]): DxfTextEntry => {
    const arr = cadAll.get(key)!;
    let best = arr[0], bd = Infinity;
    for (const t of arr) {
      const [px, py] = toPage(t);
      const d = (px - nearPt[0]) ** 2 + (py - nearPt[1]) ** 2;
      if (d < bd) { bd = d; best = t; }
    }
    return best;
  };
  const classify = (normalized: string) => classifyLabel(normalized, customPatterns) ?? undefined;

  const stats: FixStats = { adoptions: 0, splits: 0, additions: 0, skippedAmbiguous: 0, spaceVariants: 0 };
  const out: LayoutTextEntity[] = [];
  let seq = 0;
  const stamp = Date.now();
  // CAD labels consumed by an adoption or split this pass — the additions
  // loop must not re-add them as "missing" (they now exist in the layout).
  const consumed = new Set<string>();

  for (const e of entities) {
    const key = normalizeLabel(e.normalizedText);
    if (!key || cadAll.has(key) || e.cadFix) {
      out.push(e); // matched, unfixable, or already fixed — leave alone
      continue;
    }
    const original = {
      text: e.text, normalizedText: e.normalizedText,
      cx: e.cx, cy: e.cy, width: e.width, height: e.height,
      fontSize: e.fontSize, rotation: e.rotation,
      classification: e.classification, merged: e.merged,
    };

    // 1) space-variant recognition (must precede split — the same label in
    // a different whitespace canon must not be torn in two).
    const skMatches = spaceless.get(key.replace(/ /g, ''));
    if (skMatches && skMatches.size >= 1) {
      stats.spaceVariants++;
      out.push(e); // same label; nothing to fix
      continue;
    }
    // 2) unambiguous truncation completion
    let adoptKey: string | null = null;
    if (key.length >= 4) {
      const cands: string[] = [];
      for (const ck of cadAll.keys()) {
        if (ck.length > key.length && (ck.startsWith(key) || ck.endsWith(key))) {
          cands.push(ck);
          if (cands.length > 1) break;
        }
      }
      if (cands.length === 1) adoptKey = cands[0];
      else if (cands.length > 1) stats.skippedAmbiguous++;
    }
    if (adoptKey) {
      consumed.add(adoptKey);
      const inst = nearestInstance(adoptKey, [e.cx, e.cy]);
      const [cx, cy] = toPage(inst);
      const normalized = classifyNormalize(inst.t);
      out.push({
        ...e,
        text: inst.t, normalizedText: normalized,
        cx, cy,
        classification: classify(normalized),
        cadFix: { kind: 'adopt', original },
      });
      stats.adoptions++;
      continue;
    }

    // 3) confirmed-merge split: key = A + B (optionally around a space)
    let split: [string, string] | null = null;
    for (let cut = 2; cut < key.length - 1 && !split; cut++) {
      const a = key.slice(0, cut), b = key.slice(cut);
      if (cadAll.has(a) && cadAll.has(b)) split = [a, b];
      else {
        const a2 = a.trimEnd(), b2 = b.trimStart();
        if (a2 !== a && cadAll.has(a2) && cadAll.has(b2)) split = [a2, b2];
      }
    }
    if (split) {
      const group = `cadfix-${stamp}-${seq++}`;
      const parts = split.map((partKey, i) => {
        consumed.add(partKey);
        const inst = nearestInstance(partKey, [e.cx, e.cy]);
        const [cx, cy] = toPage(inst);
        const normalized = classifyNormalize(inst.t);
        const fontSize = Math.max((inst.h ?? 0.3) * scale, 1);
        return {
          id: `${group}-${i}`,
          text: inst.t, normalizedText: normalized,
          cx, cy,
          width: 0.6 * fontSize * normalized.length, height: fontSize,
          fontSize, rotation: inst.r ?? 0,
          classification: classify(normalized),
          cadFix: i === 0
            ? { kind: 'split-main' as const, group, original }
            : { kind: 'split-part' as const, group },
        } as LayoutTextEntity;
      });
      out.push(...parts);
      stats.splits++;
      continue;
    }

    out.push(e); // extractor-only with no confirmed reconstruction
  }

  // 4) additions: CAD-unique, absent from extraction, classifies to a
  // topology level worth having.
  for (const [key, arr] of cadAll) {
    if (arr.length !== 1 || extKeys.has(key) || consumed.has(key)) continue;
    const inst = arr[0];
    const normalized = classifyNormalize(inst.t);
    const cls = classify(normalized);
    if (!cls || ADD_LEVELS_EXCLUDED.has(cls.level)) continue;
    const [cx, cy] = toPage(inst);
    const fontSize = Math.max((inst.h ?? 0.3) * scale, 1);
    out.push({
      id: `cadfix-${stamp}-add-${seq++}`,
      text: inst.t, normalizedText: normalized,
      cx, cy,
      width: 0.6 * fontSize * normalized.length, height: fontSize,
      fontSize, rotation: inst.r ?? 0,
      classification: cls,
      cadFix: { kind: 'added' },
    } as LayoutTextEntity);
    stats.additions++;
  }

  if (stats.adoptions + stats.splits + stats.additions === 0) {
    return { entities, stats };
  }
  return { entities: out, stats };
}

/** Pure: revert every CAD fix. Exported for tests. */
export function computeUndoCadFixes(entities: LayoutTextEntity[]): { entities: LayoutTextEntity[]; reverted: number } {
  let reverted = 0;
  const out: LayoutTextEntity[] = [];
  for (const e of entities) {
    if (!e.cadFix) { out.push(e); continue; }
    reverted++;
    if (e.cadFix.kind === 'added' || e.cadFix.kind === 'split-part') continue; // drop
    if (e.cadFix.kind === 'split-main' || e.cadFix.kind === 'adopt') {
      const { cadFix, ...rest } = e;
      out.push({ ...rest, ...(cadFix.original ?? {}) });
    }
  }
  return { entities: out, reverted };
}

function targetLayout(overlay: Overlay): SiteLayout | null {
  const reg = overlay.dxf?.registration;
  if (overlay.dxf?.status !== 'ready' || reg?.kind !== 'layout') return null;
  const data = useProjectStore.getState().data;
  const layouts = data.siteLayouts ?? (data.siteLayout ? [data.siteLayout] : []);
  return layouts.find((l) => l.id === reg.layoutId) ?? null;
}

function writeEntities(layout: SiteLayout, entities: LayoutTextEntity[]): void {
  const st = useProjectStore.getState();
  if (st.activeLayoutId !== layout.id) st.setActiveLayoutId(layout.id);
  useProjectStore.getState().updateSiteLayoutEntities(entities);
}

/** Apply CAD text fixes to the registration layout. */
export async function fixLabelsFromCad(overlay: Overlay): Promise<FixStats | null> {
  const layout = targetLayout(overlay);
  const dxf = overlay.dxf;
  if (!layout || !dxf) return null;
  const textsDoc = await fetchIngestJson<{ texts: DxfTextEntry[] }>(dxf.outPrefix, 'texts.json');
  if (!textsDoc?.texts?.length) return null;
  const result = computeCadFixes(layout.textEntities, textsDoc.texts, layout.classificationConfig);
  if (!result) return null;
  if (result.entities !== layout.textEntities) writeEntities(layout, result.entities);
  useProjectStore.getState().flushPendingEntityWrites().catch(() => {});
  return result.stats;
}

/** Revert all CAD text fixes on the registration layout. */
export function undoCadFixes(overlay: Overlay): number {
  const layout = targetLayout(overlay);
  if (!layout) return 0;
  const { entities, reverted } = computeUndoCadFixes(layout.textEntities);
  if (reverted > 0) writeEntities(layout, entities);
  return reverted;
}

/** Whether the registration layout carries any CAD fixes. */
export function hasCadFixes(overlay: Overlay): boolean {
  const layout = targetLayout(overlay);
  return !!layout?.textEntities.some((e) => e.cadFix);
}
